import { useState } from "react";
import { formatCurrency } from "@/components/Layout";
import { useAuth } from "@/App";
import { 
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Wallet,
  Shield,
  TrendingUp,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import UpgradeModal, { TRIGGER_SOURCES } from "@/components/UpgradeModal";

// Calculate status based on percentage of income
const getStatus = (safeToSpend, currentBalance, mustCover) => {
  // If we have no balance info or it's zero, handle edge case
  if (currentBalance <= 0) {
    return {
      type: 'risk',
      label: 'Risk of shortfall',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      icon: AlertTriangle
    };
  }
  
  // Calculate percentage of balance that's safe to spend
  const safePercentage = (safeToSpend / currentBalance) * 100;
  
  if (safeToSpend <= 0 || safePercentage < 0) {
    return {
      type: 'risk',
      label: 'Risk of shortfall',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      icon: AlertTriangle
    };
  } else if (safePercentage < 10) {
    return {
      type: 'tight',
      label: 'This week is tight',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      icon: AlertCircle
    };
  } else if (safePercentage < 25) {
    return {
      type: 'managing',
      label: "You're managing",
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      icon: CheckCircle
    };
  } else {
    return {
      type: 'safe',
      label: "You're on track",
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      icon: CheckCircle
    };
  }
};

export const FinancialPositionPanel = ({ 
  position, 
  currency, 
  survivalMode, 
  onToggleSurvival,
  onUpdateBalance,
  isEditingBalance,
  setIsEditingBalance,
  balanceInput,
  setBalanceInput
}) => {
  const [expanded, setExpanded] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const { user } = useAuth();
  
  if (!position) return null;
  
  const { 
    current_balance, 
    safe_to_spend, 
    must_cover_first, 
    upcoming_obligations, 
    debt_remaining 
  } = position;
  
  const status = getStatus(safe_to_spend, current_balance, must_cover_first);
  const StatusIcon = status.icon;

  // Show "Increase this" CTA when safe-to-spend is low but not in shortfall
  const showIncreaseCTA = safe_to_spend > 0 && safe_to_spend < current_balance * 0.3 && !user?.is_premium;
  
  const handleIncreaseSafeToSpend = () => {
    setUpgradeModalOpen(true);
  };
  
  return (
    <>
      <div className="mb-6" data-testid="financial-position-panel">
        {/* Hero Card - Safe to Spend */}
        <div className={`p-6 rounded-2xl border-2 ${status.bgColor} ${status.borderColor} transition-all`}>
          {/* Top Row: Label + Survival Mode Toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-p2p-muted">Safe to Spend</span>
              <span className="text-xs text-p2p-muted/60">• Next 7 days</span>
            </div>
            
            {/* Survival Mode Badge */}
            <button
              onClick={onToggleSurvival}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                survivalMode 
                  ? 'bg-amber-500 text-white shadow-sm' 
                  : 'bg-white/80 text-p2p-muted hover:bg-white border border-p2p-border/50'
              }`}
              data-testid="survival-mode-toggle"
            >
              <Shield className="w-3.5 h-3.5" />
              {survivalMode ? 'Survival Mode ON' : 'Survival Mode'}
            </button>
          </div>
          
          {/* Hero Amount */}
          <div className="flex items-baseline gap-3 mb-3">
            <span className={`font-mono text-5xl font-bold tracking-tight ${status.color}`}>
              {formatCurrency(Math.max(0, safe_to_spend), currency)}
            </span>
          </div>
          
          {/* Status Message */}
          <div className={`flex items-center gap-2 ${status.color}`}>
            <StatusIcon className="w-5 h-5" />
            <span className="text-base font-semibold">{status.label}</span>
          </div>

          {/* Increase this CTA - Conversion trigger */}
          {showIncreaseCTA && (
            <Button
              onClick={handleIncreaseSafeToSpend}
              className="mt-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm h-9 px-4"
              data-testid="increase-safe-to-spend-btn"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Increase this
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          {/* Expand/Collapse Toggle */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 mt-4 text-sm text-p2p-muted hover:text-p2p-teal transition-colors"
            data-testid="expand-breakdown-btn"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                <span>Hide breakdown</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                <span>View breakdown</span>
              </>
            )}
          </button>
        </div>
        
        {/* Expandable Breakdown Section */}
        {expanded && (
          <div className="mt-3 p-5 rounded-2xl bg-white border border-p2p-border/50 shadow-sm animate-fade-in">
            {/* What I Have */}
            <div className="flex items-center justify-between py-3 border-b border-p2p-border/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-p2p-teal/10 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-p2p-teal" />
                </div>
                <div>
                  <p className="text-sm font-medium text-p2p-teal">What I Have</p>
                  <p className="text-xs text-p2p-muted">Current balance</p>
                </div>
              </div>
              {isEditingBalance ? (
                <div className="flex items-center gap-2">
                  <span className="text-p2p-muted">£</span>
                  <input
                    type="number"
                    value={balanceInput}
                    onChange={(e) => setBalanceInput(e.target.value)}
                    onBlur={() => {
                      onUpdateBalance(parseFloat(balanceInput) || 0);
                      setIsEditingBalance(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onUpdateBalance(parseFloat(balanceInput) || 0);
                        setIsEditingBalance(false);
                      }
                      if (e.key === 'Escape') {
                        setIsEditingBalance(false);
                      }
                    }}
                    className="w-28 font-mono text-lg font-bold text-p2p-teal bg-transparent border-b-2 border-p2p-teal outline-none text-right"
                    autoFocus
                    data-testid="balance-input"
                  />
                </div>
              ) : (
                <button
                  onClick={() => {
                    setBalanceInput(current_balance.toString());
                    setIsEditingBalance(true);
                  }}
                  className="font-mono text-lg font-bold text-p2p-teal hover:text-p2p-teal/80 transition-colors"
                  data-testid="current-balance-btn"
                >
                  {formatCurrency(current_balance, currency)}
                </button>
              )}
            </div>
            
            {/* Must Cover */}
            <div className="flex items-center justify-between py-3 border-b border-p2p-border/30">
              <div>
                <p className="text-sm font-medium text-p2p-muted">Must Cover</p>
                <p className="text-xs text-p2p-muted/70">Bills + debt minimums</p>
              </div>
              <span className={`font-mono text-lg font-semibold ${
                current_balance < must_cover_first ? 'text-red-500' : 'text-amber-600'
              }`}>
                {formatCurrency(must_cover_first, currency)}
              </span>
            </div>
            
            {/* Payment Priorities */}
            {upcoming_obligations && (
              <div className="py-3 border-b border-p2p-border/30">
                <p className="text-xs font-medium text-p2p-muted uppercase tracking-wider mb-3">Payment Priorities</p>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <span className="text-sm text-p2p-muted">Essentials</span>
                    </div>
                    <span className="text-sm font-mono font-medium text-red-600">
                      {formatCurrency(upcoming_obligations.essential, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                      <span className="text-sm text-p2p-muted">Debt Minimums</span>
                    </div>
                    <span className="text-sm font-mono font-medium text-purple-600">
                      {formatCurrency(upcoming_obligations.debt_minimum, currency)}
                    </span>
                  </div>
                  <div className={`flex items-center justify-between ${survivalMode ? 'opacity-40' : ''}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                      <span className="text-sm text-p2p-muted">Lifestyle</span>
                    </div>
                    <span className="text-sm font-mono text-p2p-muted">
                      {formatCurrency(upcoming_obligations.lifestyle, currency)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Debt Remaining */}
            {debt_remaining > 0 && (
              <div className="flex items-center justify-between pt-3">
                <div>
                  <p className="text-sm font-medium text-p2p-muted">Total Debt Remaining</p>
                </div>
                <span className="font-mono text-lg font-semibold text-red-500">
                  {formatCurrency(debt_remaining, currency)}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Survival Mode Active Warning */}
        {survivalMode && !expanded && (
          <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3">
            <Shield className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-700">
              <span className="font-medium">Survival Mode:</span> Focus on essentials and debt minimums only
            </p>
          </div>
        )}
      </div>

      {/* Upgrade Modal for Safe-to-Spend trigger */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        triggerSource={TRIGGER_SOURCES.SAFE_TO_SPEND}
        contextData={{ safe_to_spend_amount: safe_to_spend }}
      />
    </>
  );
};

export default FinancialPositionPanel;
